import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';
import { Users, MessageSquare, UserPlus, UserMinus, Mic } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="stat-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">{label}</p>
          <p className="text-4xl font-mono-stats font-bold text-white">{value ?? '—'}</p>
          {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: accent + '22' }}>
          <Icon size={20} style={{ color: accent }} />
        </div>
      </div>
    </motion.div>
  );
}

export default function ServerStats() {
  const { selectedGuildId } = useGuild();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedGuildId) return;
    setLoading(true);
    axios.get(`${API}/api/servers/stats`, { params: { guild_id: selectedGuildId } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedGuildId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="server-stats-main">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-[Outfit] text-white">Server Stats</h1>
            <p className="text-zinc-500 text-sm mt-1 font-[Manrope]">Member activity and server growth analytics</p>
          </div>
          {data?.guild_info && (
            <div className="hidden sm:flex items-center gap-3 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl px-4 py-2" data-testid="guild-info">
              {data.guild_info.icon && (
                <img src={data.guild_info.icon} alt="server icon" className="w-8 h-8 rounded-full" />
              )}
              <div>
                <p className="text-white text-sm font-semibold font-[Outfit]">{data.guild_info.name}</p>
                <p className="text-zinc-500 text-xs">{data.guild_info.member_count?.toLocaleString()} members</p>
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={UserPlus} label="Total Joins" value={(data?.total_joins || 0).toLocaleString()} sub="All time" accent="#10B981" />
          <StatCard icon={UserMinus} label="Total Leaves" value={(data?.total_leaves || 0).toLocaleString()} sub="All time" accent="#EF4444" />
          <StatCard icon={MessageSquare} label="Messages" value={(data?.total_messages || 0).toLocaleString()} sub="Tracked" accent="#5865F2" />
          <StatCard icon={Mic} label="Voice Events" value={(data?.total_voice_events || 0).toLocaleString()} sub="Joins & leaves" accent="#F59E0B" />
        </div>

        {/* Member Growth Chart */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 mb-6"
          data-testid="member-growth-area-chart"
        >
          <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Member Activity (30 days)</h3>
          <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Joins, leaves and net growth</p>
          {(data?.growth_data || []).every(d => d.joins === 0 && d.leaves === 0) ? (
            <p className="text-zinc-500 text-sm text-center py-12">No member events tracked yet. Make sure the bot has Server Members Intent enabled.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data?.growth_data || []}>
                <defs>
                  <linearGradient id="joinsGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="leavesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} interval={4} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ color: '#A1A1AA', fontSize: 12 }} />
                <Area type="monotone" dataKey="joins" name="Joins" stroke="#10B981" fill="url(#joinsGrad2)" strokeWidth={2} />
                <Area type="monotone" dataKey="leaves" name="Leaves" stroke="#EF4444" fill="url(#leavesGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Retention */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 mb-6"
          data-testid="retention-card"
        >
          <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Member Retention</h3>
          <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Of members who joined in the last 30 days, how many are still here</p>
          {!data?.retention?.new_members ? (
            <p className="text-zinc-500 text-sm text-center py-6">No new members in the last 30 days</p>
          ) : (
            <div className="flex items-center gap-6">
              <div className="relative w-20 h-20 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                  <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="16" fill="none" stroke="#10B981" strokeWidth="3"
                    strokeDasharray={`${(data.retention.rate || 0)} 100`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-mono-stats font-bold text-sm">{data.retention.rate ?? '—'}%</span>
                </div>
              </div>
              <div className="flex gap-8">
                <div>
                  <p className="text-2xl font-mono-stats font-bold text-white">{data.retention.new_members}</p>
                  <p className="text-xs text-zinc-500 font-[Manrope]">New members (30d)</p>
                </div>
                <div>
                  <p className="text-2xl font-mono-stats font-bold text-emerald-400">{data.retention.retained}</p>
                  <p className="text-xs text-zinc-500 font-[Manrope]">Still in server</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Message Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="message-activity-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Messages by Hour</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Peak activity times</p>
            {(data?.hourly_messages || []).every(h => h.messages === 0) ? (
              <p className="text-zinc-500 text-sm text-center py-12">No messages tracked yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data?.hourly_messages || []}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="hour" tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} interval={3} />
                  <YAxis tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="messages" name="Messages" fill="#5865F2" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          {/* Net Growth */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="net-growth-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Net Growth</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Daily net member change (30 days)</p>
            {(data?.growth_data || []).every(d => d.net === 0) ? (
              <p className="text-zinc-500 text-sm text-center py-12">No growth data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={(data?.growth_data || []).slice(-14)}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} interval={2} />
                  <YAxis tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="net" name="Net Change" radius={[3, 3, 0, 0]}
                    fill="#10B981"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
